import { validateDoorConfig } from './door-model';
import { analyzeTcnCompatibility } from './configurator-tcn';
import type {
  ConfiguratorState,
  ConfiguratorUser,
  GeneratedOutputs,
  OutputArtifactKind,
  OutputArtifactState,
} from './configurator-types';

export const OUTPUT_KEYS = ['quote', 'drawing', 'config', 'tcn'] as const;
export const WORKFLOW_SCHEMA_VERSION = 2;

export type OutputKey = (typeof OUTPUT_KEYS)[number];

export function createEmptyOutputs(): GeneratedOutputs {
  return {
    quote: null,
    drawing: null,
    config: null,
    tcn: null,
  };
}

export function createOutputArtifact(
  fingerprint: string,
  kind: OutputArtifactKind,
  details: Pick<OutputArtifactState, 'containsMachining' | 'releaseState'> = {},
): OutputArtifactState {
  return {
    fingerprint,
    generatedAt: new Date().toISOString(),
    kind,
    ...details,
  };
}

export function isOutputCurrent(
  output: OutputArtifactState | null | undefined,
  fingerprint: string,
) {
  return Boolean(output && output.fingerprint === fingerprint);
}

export function migrateStoredWorkflowStep(rawStep: number, storedVersion: number) {
  const normalizedStep = Number.isFinite(rawStep) ? Math.trunc(rawStep) : 1;
  const migratedStep =
    storedVersion >= WORKFLOW_SCHEMA_VERSION
      ? normalizedStep
      : normalizedStep >= 6
        ? 5
        : normalizedStep === 5
          ? 4
          : normalizedStep;

  return Math.max(1, Math.min(5, migratedStep));
}

export function validateContact(config: ConfiguratorState) {
  const nameValid = config.customerName.trim().length >= 2;
  const email = config.customerEmail.trim();
  const emailValid = isValidEmail(email);

  return {
    nameValid,
    emailValid,
    consentValid: config.consent,
    valid: nameValid && emailValid && config.consent,
  };
}

export function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(email.trim());
}

export function canContinueStep(
  step: number,
  config: ConfiguratorState,
  _outputs: GeneratedOutputs,
) {
  if (step === 3) {
    return !getBlockingConfigurationIssues(config).length;
  }
  if (step === 5) return canSubmit(config, _outputs);
  return true;
}

/** A target step is reachable only when every earlier gate is complete. */
export function canEnterStep(
  targetStep: number,
  config: ConfiguratorState,
  outputs: GeneratedOutputs,
) {
  const boundedTarget = Math.max(1, Math.min(5, Math.trunc(targetStep)));
  for (let step = 1; step < boundedTarget; step += 1) {
    if (!canContinueStep(step, config, outputs)) return false;
  }
  return true;
}

export function canSubmit(config: ConfiguratorState, _outputs: GeneratedOutputs) {
  void _outputs;
  if (getBlockingConfigurationIssues(config).length) return false;
  return validateContact(config).valid;
}

export function getContinueHint(
  step: number,
  config: ConfiguratorState,
  _outputs: GeneratedOutputs,
) {
  void _outputs;
  if (step === 3) {
    return getBlockingConfigurationIssues(config)[0]?.message ?? '';
  }
  if (step === 5) {
    const contact = validateContact(config);
    if (!contact.nameValid || !contact.emailValid) return 'Doplňte meno a platný e-mail';
    if (!contact.consentValid) return 'Potvrďte súhlas so spracovaním údajov';
  }
  return '';
}

function getBlockingConfigurationIssues(config: ConfiguratorState) {
  const issues = config.includeTcnFile
    ? analyzeTcnCompatibility(config).issues
    : validateDoorConfig(config);
  return issues.filter(issue => issue.level === 'error');
}

export function parseStoredUser(value: string | null): ConfiguratorUser | null {
  if (!value) return null;
  try {
    const candidate: unknown = JSON.parse(value);
    if (!isRecord(candidate)) return null;
    const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';
    const email = typeof candidate.email === 'string' ? candidate.email.trim() : '';
    const mode = candidate.mode === 'account' || candidate.mode === 'guest' ? candidate.mode : null;
    if (!name || !mode || !isValidEmail(email)) return null;
    return {
      name,
      email,
      mode,
      ...(typeof candidate.company === 'string' && candidate.company.trim()
        ? { company: candidate.company.trim() }
        : {}),
    };
  } catch {
    return null;
  }
}

export function parseStoredOutputs(value: unknown): GeneratedOutputs {
  const parsed = createEmptyOutputs();
  if (!isRecord(value)) return parsed;

  for (const key of OUTPUT_KEYS) {
    const artifact = value[key];
    if (!isRecord(artifact)) continue;
    if (
      typeof artifact.fingerprint !== 'string' ||
      typeof artifact.generatedAt !== 'string' ||
      !isOutputKind(artifact.kind)
    ) {
      continue;
    }
    parsed[key] = {
      fingerprint: artifact.fingerprint,
      generatedAt: artifact.generatedAt,
      kind: artifact.kind,
      ...(typeof artifact.containsMachining === 'boolean'
        ? { containsMachining: artifact.containsMachining }
        : {}),
      ...(isReleaseState(artifact.releaseState) ? { releaseState: artifact.releaseState } : {}),
    };
  }
  return parsed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOutputKind(value: unknown): value is OutputArtifactKind {
  return (
    value === 'generated-preview' ||
    value === 'downloaded' ||
    value === 'parametric-program-review' ||
    value === 'reference-program-review'
  );
}

function isReleaseState(value: unknown): value is NonNullable<OutputArtifactState['releaseState']> {
  return (
    value === 'reference-match-review-required' ||
    value === 'parametric-program-review-required' ||
    value === 'invalid-configuration'
  );
}
